import { alpha, createTheme } from "@mui/material/styles";

const BRAND_PRIMARY = "#2b61aa";
const BRAND_PRIMARY_DARK = "#4f8fe6";
const BRAND_ACCENT = "#17a2fb";
const SUCCESS_MAIN = "rgb(0, 176, 35)";
const WARNING_MAIN = "#fe950d";

const getDesignTokens = (mode) => {
  const isDark = mode === "dark";

  return {
    palette: {
      mode,
      primary: {
        main: isDark ? BRAND_PRIMARY_DARK : BRAND_PRIMARY,
        dark: isDark ? "#3574cb" : "#1f4f8a",
        light: isDark ? "#7fb2ff" : "#5f8fcb",
        contrastText: "#ffffff",
      },
      secondary: {
        main: isDark ? "#4caf50" : SUCCESS_MAIN,
        contrastText: "#ffffff",
      },
      success: {
        main: SUCCESS_MAIN,
        light: alpha(SUCCESS_MAIN, 0.18),
        dark: "#1f6b03",
        contrastText: "#ffffff",
      },
      warning: {
        main: WARNING_MAIN,
        light: alpha(WARNING_MAIN, 0.18),
        dark: "#d97706",
        contrastText: "#ffffff",
      },
      error: {
        main: "#d32f2f",
      },
      info: {
        main: BRAND_ACCENT,
      },
      background: {
        default: isDark ? "#161c27" : "#f4f7fb",
        paper: isDark ? "#1e2633" : "#ffffff",
        navbarBg: isDark ? "#1b2330" : BRAND_PRIMARY,
        sideBg: isDark ? "#17202d" : BRAND_PRIMARY,
        actBn: isDark ? "#263447" : "#d6dde8",
        bgCustomPrimary: isDark ? alpha(BRAND_PRIMARY_DARK, 0.14) : "#e7eefb",
        bgCustomSecondary: isDark ? alpha(WARNING_MAIN, 0.14) : "#fff0bf",
        bgCustomSuccess: isDark ? alpha(SUCCESS_MAIN, 0.14) : "#c4ffca",
        commentBg: isDark ? "#222c3a" : "#fdfdfd",
        sectionBg: isDark ? "sectionbg" : "#f8f9fa",

        
      },
      text: {
        primary: isDark ? "#f5f7fa" : "#16202a",
        secondary: isDark ? "#aeb9c6" : "#4c5a69",
        disabled: isDark ? "#7b8794" : "#8a97a6",
        header: "#ffffff",
        active: BRAND_ACCENT,
        bread: isDark ? "#dbe7f6" : BRAND_PRIMARY,
      },
      divider: isDark ? alpha("#dbe7f6", 0.12) : "#d7e0ea",
      action: {
        hover: isDark ? alpha("#ffffff", 0.08) : alpha(BRAND_PRIMARY, 0.08),
        selected: isDark ? alpha("#ffffff", 0.14) : alpha("#ffffff", 0.74),
        focus: isDark ? alpha("#ffffff", 0.16) : alpha(BRAND_PRIMARY, 0.14),
      },
      admin: { main: BRAND_PRIMARY },
      sre: { main: "#009688" },
      finops: { main: "#ff5722" },
      customColors: {
        green: "#4caf50",
      },
      borderColor: {
        defaultBorder: isDark ? alpha("#dbe7f6", 0.16) : "#0562bf",
        cardborder: isDark ? alpha("#dbe7f6", 0.12) : "#dfe6ef",
      },
    },
    customGradients: {
      pageBackground: isDark
        ? "linear-gradient(135deg, #121925 0%, #192332 48%, #111822 100%) fixed"
        : "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 50%, #f8fafc 100%) fixed",
    },
  };
};

const getTheme = (mode = "light") => {
  const tokens = getDesignTokens(mode);

  return createTheme({
    ...tokens,
    typography: {
      fontFamily: '"Shippori Antique B1", sans-serif',
      fontSize: 14,
      h5: { fontSize: "14px", fontWeight: 500 },
      h6: { fontSize: "14px", fontWeight: 500 },
      breadcrumbs: { fontSize: "12px", fontWeight: 500 },
      title: { fontSize: "16px", fontWeight: 600, color: tokens.palette.primary.main },
      body1: { fontSize: "14px" },
      body2: { fontSize: "12px" },
      button: { fontSize: "14px", textTransform: "initial" },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: (theme) => ({
          ":root": {
  colorScheme: theme.palette.mode,
  "--app-bg-default": theme.palette.background.default,
  "--app-bg-paper": theme.palette.background.paper,
  "--app-bg-sidebar": theme.palette.background.sideBg,
  "--app-bg-soft": theme.palette.background.bgCustomPrimary,
  "--app-text-primary": theme.palette.text.primary,
  "--app-text-secondary": theme.palette.text.secondary,
  "--app-header-text": theme.palette.text.header,
  "--app-section-bg":  theme.palette.background.sectionBg,

  "--app-primary": theme.palette.primary.main,
  "--app-primary-hover": theme.palette.primary.dark,

  "--app-divider": theme.palette.divider,

  "--app-card-disabled":
  theme.palette.mode === "dark"
    ? "rgba(219, 231, 246, 0.12)"
    : "#eee",

  "--app-overlay":
    theme.palette.mode === "dark"
      ? "rgba(0,0,0,0.62)"
      : "rgba(0,0,0,0.35)",

  "--app-shadow-soft":
    theme.palette.mode === "dark"
      ? "0 2px 6px rgba(0,0,0,0.5)"
      : "0 2px 6px rgba(0,0,0,0.15)",

      "--app-icon-color":
  theme.palette.mode === "dark"
    ? "#5A687D"
    : "#2b61aa",

  "--app-sidebar-active": theme.palette.action.selected,
  "--app-sidebar-hover": theme.palette.action.hover,
},
          body: {
            margin: 0,
            overflowY: "hidden",
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
          },
          "#root": {
            minHeight: "100vh",
            backgroundColor: theme.palette.background.default,
          },
        }),
      },
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundImage: "none",
          }),
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundImage: "none",
            backgroundColor: theme.palette.background.paper,
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.borderColor.cardborder}`,
            boxShadow: theme.palette.mode === "dark" ? "0 12px 28px rgba(0,0,0,0.28)" : "0 8px 24px rgba(15, 23, 42, 0.06)",
          }),
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          }),
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderColor: theme.palette.divider,
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: ({ theme }) => ({
            "& .MuiOutlinedInput-root": {
              fontSize: "14px",
              backgroundColor: theme.palette.background.paper,
              "& fieldset": {
                borderColor: theme.palette.borderColor,
              },
              "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
            },
            "& .MuiInputLabel-root": {
              fontSize: "14px",
              color: theme.palette.text.secondary,
            },
          }),
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          input: {
            fontSize: "14px",
          },
          option: {
            fontSize: "14px",
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: "14px",
            color: theme.palette.text.primary,
            ".MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.divider,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.primary.main,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.primary.main,
            },
          }),
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            height: "56px",
            fontSize: "14px",
            color: theme.palette.text.secondary,
          }),
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            fontSize: "14px",
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            fontSize: "14px",
            fontWeight: 600,
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.secondary,
            "&.Mui-checked": {
              color: theme.palette.primary.main,
            },
          }),
        },
      },
      MuiButton: {
        variants: [
          {
            props: { variant: "greenContained" },
            style: {
              backgroundColor: SUCCESS_MAIN,
              color: "#fff",
              border: `1px solid ${SUCCESS_MAIN}`,
              fontFamily: '"Poppins", sans-serif',
              textAlign: "center",
              verticalAlign: "middle",
              userSelect: "none",
              padding: "0.25rem 0.5rem",
              textTransform: "initial",
              lineHeight: 1.5,
              borderRadius: "0.25rem",
              boxShadow: "none",
              "&:hover, &:focus": {
                color: SUCCESS_MAIN,
                backgroundColor: "#fff",
                border: `1px solid ${SUCCESS_MAIN}`,
              },
            },
          },
          {
            props: { variant: "cancelContained" },
            style: {
              borderColor: "#999",
              color: "#fff",
              backgroundColor: "#999",
              fontFamily: '"Poppins", sans-serif',
              textAlign: "center",
              verticalAlign: "middle",
              userSelect: "none",
              padding: "0.2rem 0.85rem",
              textTransform: "initial",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#bbb",
                color: "#fff",
              },
            },
          },
        ],
        styleOverrides: {
          containedPrimary: ({ theme }) => ({
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }),
          containedSecondary: {
            backgroundColor: "#4caf50",
            color: "#ffffff",
            "&:hover": {
              backgroundColor: "#43a047",
            },
          },
          outlinedPrimary: ({ theme }) => ({
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            "&:hover": {
              borderColor: theme.palette.primary.dark,
              color: theme.palette.primary.dark,
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
          }),
          outlinedSecondary: {
            borderColor: "#4caf50",
            color: "#4caf50",
            "&:hover": {
              borderColor: "#43a047",
              color: "#43a047",
            },
          },
          contained: {
            lineHeight: 1.25,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
          }),
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: "14px",
            color: theme.palette.text.primary,
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
            },
          }),
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderColor: theme.palette.divider,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          }),
          columnHeaders: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }),
          footerContainer: ({ theme }) => ({
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }),
        },
      },
    },
  });
};

export default getTheme;
